import { useEffect, useState } from "react";

/**
 * Preloader — Minimal 0 → 100% loading experience.
 *
 * Design intent
 * -------------
 * The percentage itself IS the visual. No logo animation, no particle field.
 * Bold Archivo Black numeral dominates the viewport, small Figtree/Manrope
 * caption sits above and below. Palette pulled from the site's existing
 * teal / ivory / gold system — no new colours introduced.
 *
 * Progress model
 * --------------
 * We don't fake a duration. Progress advances against real browser signals:
 *   - 0% → 25%      (mount + first paint)
 *   - 25% → 65%     (document.fonts.ready)
 *   - 65% → 95%     (window.load fired)
 *   - 95% → 100%    (short settle before exit)
 *
 * Between checkpoints we smoothly interpolate toward the current target so the
 * counter never jumps or freezes. If the browser loads faster than the visible
 * counter can catch up, we simply race the counter forward at ~2%/frame.
 *
 * Non-goals
 * ---------
 *   - We never appear on inquiry navigation (session flag).
 *   - We never trap focus if animation fails (auto-exit after MAX_MS).
 *   - We respect prefers-reduced-motion (skip mask reveal, use a plain fade).
 */

const SESSION_FLAG = "pgfx-preloader-shown-v1";
const MIN_VISIBLE_MS = 620;        // Minimum time the loader is visible so 0→100 always animates.
const MAX_VISIBLE_MS = 6000;       // Safety cap — if load signals never fire, exit anyway.
const EXIT_DURATION_MS = 520;

export const Preloader = () => {
    // Guard: if we've already shown the loader in this session (SPA nav, back button),
    // never render it again. Cheap sessionStorage check.
    const [shouldRender, setShouldRender] = useState(() => {
        if (typeof window === "undefined") return false;
        try {
            return window.sessionStorage.getItem(SESSION_FLAG) !== "1";
        } catch (_e) {
            return true;
        }
    });

    const [progress, setProgress] = useState(0);
    const [exiting, setExiting] = useState(false);

    useEffect(() => {
        if (!shouldRender) return;

        const startAt = performance.now();
        let target = 22;              // First checkpoint — component mounted.
        let rafId = 0;
        let cancelled = false;

        const bumpTarget = (val) => { target = Math.max(target, Math.min(val, 100)); };

        // Real browser signals drive the checkpoints.
        const onFontsReady = () => bumpTarget(64);
        const onWindowLoad = () => {
            bumpTarget(94);
            // Settle briefly after the real load signal, then complete. Without
            // this the counter plateaus at 94% until the MAX_VISIBLE_MS safety
            // timer fires — a long, stuck-feeling pause on first visit.
            setTimeout(() => { if (!cancelled) bumpTarget(100); }, 480);
        };
        const onSafetyExit = () => bumpTarget(100);

        // document.fonts is not available in every environment — guard access.
        if (typeof document !== "undefined" && document.fonts?.ready) {
            document.fonts.ready.then(onFontsReady).catch(() => onFontsReady());
        } else {
            // No FontFaceSet API — treat fonts as ready after ~250ms.
            setTimeout(onFontsReady, 250);
        }

        if (typeof document !== "undefined" && document.readyState === "complete") {
            onWindowLoad();
        } else if (typeof window !== "undefined") {
            window.addEventListener("load", onWindowLoad, { once: true });
        }

        const safety = setTimeout(onSafetyExit, MAX_VISIBLE_MS);

        // Smooth counter interpolation. Runs until progress reaches 100.
        const tick = () => {
            if (cancelled) return;
            setProgress((cur) => {
                if (cur >= target) return cur;
                // Ease toward target — larger step when far away, smaller when close.
                const distance = target - cur;
                const step = Math.max(0.4, distance * 0.06);
                const next = Math.min(target, cur + step);
                // Once we're at 100 and the minimum visible time has passed → trigger exit.
                if (next >= 100) {
                    const elapsed = performance.now() - startAt;
                    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
                    setTimeout(() => {
                        if (!cancelled) setExiting(true);
                    }, remaining + 180);
                }
                return next;
            });
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
            clearTimeout(safety);
            if (typeof window !== "undefined") {
                window.removeEventListener("load", onWindowLoad);
            }
        };
    }, [shouldRender]);

    // Unmount after the exit animation completes.
    useEffect(() => {
        if (!exiting) return;
        try { window.sessionStorage.setItem(SESSION_FLAG, "1"); } catch (_e) { /* quota — ignore */ }
        const t = setTimeout(() => setShouldRender(false), EXIT_DURATION_MS);
        return () => clearTimeout(t);
    }, [exiting]);

    if (!shouldRender) return null;

    const rounded = Math.min(100, Math.floor(progress));

    return (
        <div
            role="progressbar"
            aria-label="Loading Pranto.Gfx"
            aria-valuenow={rounded}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="pgfx-preloader"
            className="pgfx-preloader"
            data-exiting={exiting ? "true" : "false"}
        >
            <div className="pgfx-preloader__inner">
                <div className="pgfx-preloader__eyebrow" data-testid="preloader-brand">PRANTO.GFX</div>

                <div className="pgfx-preloader__num" aria-hidden="true" data-testid="preloader-percent">
                    <span className="pgfx-preloader__digits">{rounded}</span>
                    <span className="pgfx-preloader__pct">%</span>
                </div>

                <div className="pgfx-preloader__caption">LOADING EXPERIENCE</div>

                <div className="pgfx-preloader__rail" aria-hidden="true">
                    <div
                        className="pgfx-preloader__fill"
                        style={{ transform: `scaleX(${Math.min(1, progress / 100)})` }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Preloader;
